import { useEffect, useRef } from "react";
import { getCalc } from "../lib/api";
import { looksLikeNestedPeriodFxRates } from "../lib/periodFxRates";

/**
 * Page-load restore of the workflow: the saved calc (by id), the pre-calc
 * input cache, and the reconciliation of a restored run selection.
 * Moved out of WorkflowContext.jsx unchanged in substance (A1-93, its
 * 620-line ratchet) except that the saved calc now opens through
 * openCalc() — see the comment at that call.
 */
// Mirror of the backend's `.to_list(200)` in api/routes_runs.list_ingest_runs.
export const INGEST_LIST_CAP = 200;

export const useWorkflowRestore = ({
  admin, bootedRef, initialCalcIdRef, urlCalcIdRef, setBooting, openCalc,
  runs, selectedRunIds, setSelectedRunIds, periodStart, setPeriodStart,
  periodEnd, setPeriodEnd, periodFxRates, setPeriodFxRates, clientId, setClientId, setPanelKey,
}) => {
  // v0.5.9j — re-hydrate the last calc (by id) once, after auth.
  //
  // v0.6.7 fix — this and the localStorage inputs-restore effect below
  // BOTH race WorkflowLayout.jsx's own `?calc_id=` deep-link handler on
  // mount: all three can call setSelectedRunIds/setPeriodStart/
  // setPeriodEnd, and whichever's async work resolves LAST wins,
  // independent of which one the operator actually intended. Found
  // live: navigating to a specific signed-off calc via `?calc_id=`
  // loaded that calc correctly, but this effect's OWN sessionStorage-
  // cached "last calc" (a DIFFERENT, stale id from an earlier calc in
  // the same browser) finished its own fetch afterward and overwrote
  // selectedRunIds/period with ITS values — leaving `calc` (from the
  // deep link) and selectedRunIds/period (from this effect) pointing
  // at two different calcs. `calcMatchesInputs`
  // (workflow/workflowState.js) then read run_ids/period on `calc`
  // against those mismatched selectedRunIds/period and reported the
  // calc as stale, silently re-locking Step 4/5/6 (including hiding
  // the Lock-for-filing button) even though the calc was genuinely
  // signed off. An explicit `?calc_id=` is the operator's most recent,
  // most specific intent — it must not lose this race, so this effect
  // is a no-op whenever that param is present and defers entirely to
  // WorkflowLayout.jsx's own handler.
  useEffect(() => {
    if (!admin || bootedRef.current) return;
    bootedRef.current = true;
    if (urlCalcIdRef.current) {
      setBooting(false);
      return;
    }
    const savedId = initialCalcIdRef.current;
    if (!savedId) { setBooting(false); return; }
    // A stale / deleted id is forgotten so it is not retried on every load.
    const forget = () => {
      if (sessionStorage.getItem("vat.calcId") === savedId) sessionStorage.removeItem("vat.calcId");
    };
    (async () => {
      try {
        const c = await getCalc(savedId);
        if (!c || !c.id) { forget(); return; }
        // Restore every input consistent with the calc (files, period,
        // FX, country filter) so it does not read as stale on arrival.
        // v0.5.9m/v0.6 — FX restored (normalized via the doc's `regime`).
        //
        // A1-93 (3) — and its OWN client. This used to stamp
        // calcClientId from the dropdown (clientIdRef), so after a reload
        // with another client selected — e.g. a second tab — client X's
        // figures were shown and signable under client Y. openCalc()
        // takes the client from the calc doc, as the ?calc_id= path does,
        // and the client selector stays locked while a calc is loaded.
        await openCalc(c);
      } catch { forget(); /* stale / deleted calc id */ }
      finally { setBooting(false); }
    })();
  }, [admin, openCalc]);

  // v0.6 nav refactor — cache PRE-CALC workflow inputs (run selection,
  // period, FX, client) to localStorage so a hard refresh BEFORE any
  // calc exists restores them instantly (URL-persistence gap found in
  // testing). The backend stays the source of truth: (a) when a saved
  // calc exists its boot above overrides these with the calc's values,
  // and (b) restored run ids are reconciled against the live runs list
  // below — ids the backend no longer has are dropped.
  const INPUTS_KEY = "vat.workflowInputs";
  const inputsRestoredRef = useRef(false);

  useEffect(() => {
    if (!admin || inputsRestoredRef.current) return;
    inputsRestoredRef.current = true;
    // v0.6.7 fix — same race as the boot-rehydrate effect above (see its
    // comment): this cache has no concept of a specific calc, so restoring
    // it whenever `?calc_id=` names one can overwrite the deep link's
    // freshly-set selectedRunIds/period with unrelated cached values,
    // desyncing them from the calc that just loaded.
    if (urlCalcIdRef.current) {
      return;
    }
    try {
      const raw = localStorage.getItem(INPUTS_KEY);
      if (!raw) return;
      const c = JSON.parse(raw);
      if (Array.isArray(c.selectedRunIds) && c.selectedRunIds.length)
        setSelectedRunIds(c.selectedRunIds);
      if (c.periodStart) setPeriodStart(c.periodStart);
      if (c.periodEnd) setPeriodEnd(c.periodEnd);
      // v0.6 — this path has no calc doc, so no `regime` to infer a
      // legacy flat cached value's target from. Restore only if it
      // already looks like the current nested shape; otherwise discard
      // rather than guess — the operator just re-enters that one rate,
      // far cheaper than silently misfiling it under the wrong target.
      if (looksLikeNestedPeriodFxRates(c.periodFxRates))
        setPeriodFxRates(c.periodFxRates);
      if (c.clientId) setClientId(c.clientId);
      // A1-93 review round 4: the calculate panel may already be on screen
      // (it copied the empty inputs in the same render sign-in completed),
      // so rebuild it from what was just restored — as openCalc does — or
      // it shows no files / a default period while the workflow holds the
      // restored ones, and a run from it comes out stale.
      if (setPanelKey) setPanelKey((k) => k + 1);
    } catch { /* corrupt cache → ignore */ }
  }, [admin]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      localStorage.setItem(INPUTS_KEY, JSON.stringify({
        selectedRunIds, periodStart, periodEnd, periodFxRates, clientId,
      }));
    } catch { /* quota / disabled → non-fatal */ }
  }, [selectedRunIds, periodStart, periodEnd, periodFxRates, clientId]);

  // Reconcile a restored run selection against the live runs list (the
  // backend is authoritative — drop ids it no longer has).
  //
  // v0.6.8 fix — also drop any selected run that belongs to a DIFFERENT
  // client than the one now selected. This never happened: switching
  // the client dropdown left `selectedRunIds` untouched, so a run
  // chosen while Client A was active could still be submitted alongside
  // Client B's client_id on the next calc — the backend's own
  // RUN_CLIENT_MISMATCH guard (engine/calc_service.py) is the hard
  // stop, but silently letting the request 400 is worse UX than just
  // dropping the stale selection here, matching the existing
  // live-runs reconciliation this effect already does. Runs with no
  // client_id (unattributed/legacy uploads) are left alone — they're
  // valid for any client.
  //
  // A1-93 — /runs/ingest lists the newest INGEST_LIST_CAP uploads only
  // (routes_runs.list_ingest_runs). When the list is full, an id missing
  // from it may simply be older, not deleted: dropping it made a restored
  // calc on older uploads read "files changed" and blocked its sign-off
  // with no way to clear it. Absent ids are dropped only from a complete list.
  useEffect(() => {
    const live = new Map((runs || []).map((r) => [r.id, r]));
    if (!live.size || !selectedRunIds.length) return;
    const complete = (runs || []).length < INGEST_LIST_CAP;
    const kept = selectedRunIds.filter((id) => {
      const r = live.get(id);
      if (!r) return !complete;
      return !r.client_id || r.client_id === clientId;
    });
    if (kept.length !== selectedRunIds.length) setSelectedRunIds(kept);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runs, clientId]);
};
